// src/pages/MaxsulotTarix.js
import React, { useEffect, useRef, useState } from "react";
import "./MaxsulotTarix.css";
import api from "../api";
import { useSklad } from "../SkladContext";

const actionLabels = {
  create: "Yaratildi",
  add: "Qo‘shildi",
  edit: "Tahrirlandi",
};

const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const hasNext = (res) => Boolean(res?.data?.next);

const fmtDt = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString("uz-UZ");
  } catch {
    return String(v);
  }
};

const fmtNum = (v, { max = 3 } = {}) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("uz-UZ", { minimumFractionDigits: 0, maximumFractionDigits: max });
};

export default function MaxsulotTarix() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total_qty: 0, total_cost: 0 });

  const [sellers, setSellers] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const { activeSklad } = useSklad();
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sellerFilter, setSellerFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const searchTimer = useRef(null);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setItems([]);
  }, [q, action, dateFrom, dateTo, sellerFilter, accountFilter]);

  useEffect(() => {
    (async () => {
      try {
        const [sRes, aRes] = await Promise.all([
          api.get("sotuvchi/"),
          api.get("accounts/"),
        ]);
        setSellers(Array.isArray(sRes.data) ? sRes.data : sRes.data.results || []);
        setAccounts(Array.isArray(aRes.data) ? aRes.data : aRes.data.results || []);
      } catch (e) {
        console.log("FILTER LIST LOAD ERR:", e?.response?.status, e?.response?.data);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (q) params.set("search", q);
        if (action !== "all") params.set("action", action);
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        if (sellerFilter !== "all") params.set("source_seller", sellerFilter);
        if (accountFilter !== "all") params.set("created_by", accountFilter);
        if (activeSklad && activeSklad !== "all") params.set("sklad", activeSklad);

        const res = await api.get(`maxsulot-log/?${params.toString()}`);
        const rows = asArr(res);
        const next = hasNext(res);

        if (cancelled) return;
        setItems((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setHasMore(next);
        if (res?.data?.summary) {
          setSummary(res.data.summary);
        } else if (page === 1) {
          setSummary({ total_qty: 0, total_cost: 0 });
        }
      } catch (e) {
        if (!cancelled) window.alert("Tarixni yuklashda xato.");
        console.log("LOG LOAD ERR:", e?.response?.status, e?.response?.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, q, action, dateFrom, dateTo, sellerFilter, accountFilter, activeSklad]);

  const onSearch = (e) => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setQ(val.trim()), 300);
  };
  useEffect(() => () => clearTimeout(searchTimer.current), []);

  return (
    <div className="maxsulot-tarix-page">
      <div className="fr-panel tarix-card">
        <div className="tarix-head">
          <div className="title">Maxsulot o‘zgarishlari</div>
          <div className="filters">
            <input
              className="search-input"
              placeholder="Mahsulot, seriya, izoh..."
              value={searchText}
              onChange={onSearch}
            />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)}>
              <option value="all">Haridor: Barchasi</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.ismi || "").trim() || "Nomsiz"}{s.Dokonnomi ? ` - ${s.Dokonnomi}` : ""}
                </option>
              ))}
            </select>
            <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
              <option value="all">Accaunt: Barchasi</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username || `ID ${a.id}`}
                </option>
              ))}
            </select>
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="all">Barchasi</option>
              <option value="create">Yaratish</option>
              <option value="add">Qo‘shish</option>
              <option value="edit">Tahrirlash</option>
            </select>
          </div>
        </div>

        <div className="summary-row">
          <div className="sum-card">
            <div className="sum-label">Jami kiritilgan soni</div>
            <div className="sum-value">{fmtNum(summary?.total_qty)}</div>
          </div>
          <div className="sum-card">
            <div className="sum-label">Jami tan narx summasi</div>
            <div className="sum-value">{fmtNum(summary?.total_cost)} so'm</div>
          </div>
        </div>

        <div className="tarix-table">
          <div className="trow thead">
            <div className="col dt">Sana/vaqt</div>
            <div className="col action">Amal</div>
            <div className="col product">Mahsulot</div>
            <div className="col seller">Kimdan</div>
            <div className="col note">Izoh</div>
            <div className="col user">Accaunt</div>
            <div className="col qty">Soni</div>
            <div className="col cost">Tan narxi</div>
            <div className="col total">Jami</div>
          </div>

          {items.map((it) => (
            <div className="trow" key={it.id}>
              <div className="col dt">{fmtDt(it.created_at)}</div>
              <div className={`col action ${it.action || ""}`}>
                {it.action_label || actionLabels[it.action] || it.action || "-"}
              </div>
              <div className="col product">
                <div className="p-name">{it.product_name || "-"}</div>
                <div className="p-seria">{it.product_series || ""}</div>
              </div>
              <div className="col seller">{it.source_seller_name || "-"}</div>
              <div className="col note">{it.note || "-"}</div>
              <div className="col user">{it.created_by_username || "-"}</div>
              <div className="col qty">{it.qty != null ? fmtNum(it.qty) : "-"}</div>
              <div className="col cost">{it.unit_cost != null ? fmtNum(it.unit_cost) : "-"}</div>
              <div className="col total">{it.line_total != null ? fmtNum(it.line_total) : "-"}</div>
            </div>
          ))}
        </div>

        {loading && <div className="loading">Yuklanmoqda...</div>}
        {!loading && hasMore && (
          <div className="load-more">
            <button className="add-btn" onClick={() => setPage((p) => p + 1)}>
              Ko‘proq yuklash
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
